import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form943DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 941 Penalties: Late Filing, Late Payment & Deposits"
          description="Learn about IRS Form 941 penalties, including late filing, late payment, and late deposit. Avoid penalties and stay IRS compliant! File easily with TaxZerone!"
          keywords=""
          canonical="resources/employment-tax/form-941-penalties/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 941 </span>
          </span>{' '}
          Penalties
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Avoid costly IRS penalties and file Form 941 accurately on time!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941instructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-penalties/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941 Penalties at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        {'Employers who file '}
                        <b>Form 941, Employer’s Quarterly Federal Tax Return</b>
                        {', must '}
                        submit
                        {
                          ' the return and deposit payroll taxes on time to avoid IRS penalties. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Late Filing Penalty:</b> <b>{'5% '}</b>
                        of unpaid tax per month or partial month
                        {', up to '}
                        <b>25%</b>
                        {'. '}
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Late Payment Penalty:</b> <b>{'0.5% '}</b>o f unpaid
                        tax per month or partial month ,{' up to '}
                        <b>25%</b>
                        {'. '}
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Failure to Deposit Penalty:</b> <b>2% to 15%</b>
                        {
                          ' of the unpaid deposit, depending on how late the deposit is. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Accuracy-Related Penalty:</b> {'Generally,'}{' '}
                        <b>20% of the underpaid tax</b>
                        {
                          ' when the underpayment results from negligence or certain reporting errors. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Penalty Abatement:</b>
                        {' You may request relief by '}
                        submitting <b>Form 843</b>
                        {' when you have reasonable cause. '}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  As an employer,{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 941
                  </Link>{' '}
                  every quarter is a key part of staying compliant with IRS
                  requirements. Whether you are a small business owner or
                  managing payroll for a large team, timely and accurate filing
                  helps you avoid penalties and ensures smooth tax operations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  This resource breaks down everything you need to know about
                  Form 941 penalties, how they are calculated, and how to avoid
                  them.
                </Typography>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box />
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 941?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form" sx={{ color: '#0202ff' }}>
                          Form 941 Penalties
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to Avoid Penalties?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#receive"
                          id="what"
                          sx={{ color: '#0202ff' }}
                        >
                          What to Do If You Receive a Penalty Notice?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#penalty"
                          id="what"
                          sx={{ color: '#0202ff' }}
                        >
                          How to Request Penalty Abatement?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  What is Form 941?
                </Typography>

                <Typography variant="body1" id="when" sx={{ mb: 3 }}>
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 941
                  </Link>
                  , Employer’s Quarterly Federal Tax Return is used by employers
                  to report the taxes withheld from the employee wages such as
                  Federal Income Tax, social security Tax and Medicare taxes
                  (FICA taxes) to the IRS.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        File Form 941 Now with TaxZerone!
                      </Typography>
                      <Typography variant="body2">
                        Don’t risk IRS penalties for late filing. Use TaxZerone
                        - fast, secure, and IRS-authorized e-filing to submit
                        your Form 941 today!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941penalties`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" id="form" icon={false} sx={{ mb: 4 }}>
                  ðŸ‘‰ Need help with the form? Check out our{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 941 instructions
                  </Link>{' '}
                  for a clear, step-by-step guide.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Form 941 Penalties
                </Typography>
                <Typography variant="body1" id="key" sx={{ mb: 2 }}>
                  Businesses can face several penalties related to Form 941,
                  especially if they miss a{' '}
                  <Link
                    href="/resources/employment-tax/form-941-filing-deadlines-and-penalties-guide/"
                    sx={{ color: '#2020ff' }}
                  >
                    941 deadline
                  </Link>
                  . These include:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li> Late Filing Penalty </li>
                  <li>Late Payment Penalty</li>
                  <li> Failure to Deposit Penalty </li>
                  <li> Accuracy Related Penalty </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Late Filing Penalty
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    Form 941 Late filing penalty applies if you fail to{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-941/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      file Form 941
                    </Link>{' '}
                    by its quarterly due dates: April 30 (Q1), July 31 (Q2),
                    October 31 (Q3), or January 31 (Q4).{' '}
                  </li>
                  <li>
                    {' '}
                    <b>Penalty Rate:</b> 5% of the unpaid tax for each month or
                    partial month until the return is late and goes up to a
                    maximum of 25%.{' '}
                  </li>
                  <Alert
                    severity="info"
                    icon={<TipsAndUpdatesIcon />}
                    sx={{ mb: 4 }}
                  >
                    <b>Tips:</b> Even if you can’t pay your taxes in full,
                    filing the form on time can help you avoid the{' '}
                    <b>5% late filing penalty</b>, which adds much faster than
                    the <b>0.5% late payment penalty</b>.
                  </Alert>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Late Payment Penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 941 Late payment penalty applies when you file{' '}
                  <b>Form 941</b> on time but <b>fail to pay the taxes owed</b>{' '}
                  by the due date.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  1.Failure to pay amount shown as tax on your return:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    If you don’t pay the tax amount by the due date, a penalty
                    of <b>0.5% of the unpaid tax</b> is charged for{' '}
                    <b>each month or part of a month</b> it remains unpaid and
                    goes up to a maximum of <b>25%</b> of the unpaid
                    amount.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  2.Failure to pay tax you didn’t report on your return:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    If the IRS finds unreported tax, they will send you a notice
                    or letter that includes the amount of tax you owe and a due
                    date to pay the taxes.{' '}
                  </li>
                  <li>
                    {' '}
                    If you owe <b>less than $100,000, </b>less than $100,000,
                    you generally have <b>21 calendar days</b> from the date the
                    notice is sent to pay.{' '}
                  </li>
                  <li>
                    {' '}
                    If you owe <b>$100,000 or more</b>, you must pay within{' '}
                    <b>10 business</b> days from the date the notice is
                    sent.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Failure to Deposit Penalty
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    This penalty applies if you{' '}
                    <b>fail to deposit payroll taxes</b> (withheld from
                    employees) on time or through the proper method.{' '}
                  </li>
                  <li>
                    {' '}
                    <b>Penalty calculation:</b> It is calculated based on the
                    amount of unpaid deposit and the number of days it is late
                    after the due date.{' '}
                  </li>
                </Box>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Number of days your deposit is late
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Amount of the penalty
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>1-5 calendar days </TableCell>

                          <TableCell>2% of your unpaid deposit</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>6-15 calendar days </TableCell>

                          <TableCell>5% of your unpaid deposit </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>More than 15 calendar days </TableCell>

                          <TableCell>10% of the unpaid deposit </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            More than 10 calendar days after the first IRS
                            notice (ex: CP220 Notice) or the day you get a
                            notice or letter for immediate payment (ex: CP504J
                            Notice){' '}
                          </TableCell>

                          <TableCell>15% of your unpaid deposit </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Accuracy Related Penalty
                </Typography>

                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    This penalty applies when an employer underpays their tax
                    due to negligence or errors in reporting.{' '}
                  </li>
                  <li>
                    {' '}
                    <b>Penalty:</b> A 20% penalty applies to the portion of the
                    underpaid tax.{' '}
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        File Form 941 On Time with TaxZerone!
                      </Typography>
                      <Typography variant="body2">
                        Don’t let a missed deadline cost you. File Form 941
                        quickly and accurately with our IRS-authorized e-filing
                        service.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        id="how"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': { color: '#fff' },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941penalties`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  How to Avoid Penalties?
                </Typography>
                <Typography variant="body1">
                  You can avoid penalties by following these:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <b>File on time:</b> File your{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-941/"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS Form 941
                    </Link>{' '}
                    every quarter on time and accurately.{' '}
                  </li>
                  <li>
                    {' '}
                    <b>Deposit taxes on time:</b> Employers must deposit taxes
                    according to the{' '}
                    <Link
                      href="/resources/employment-tax/form-941-944-deposit-requirements/"
                      sx={{ color: '#2020ff' }}
                    >
                      IRS deposit schedule
                    </Link>{' '}
                    — either monthly or semi-weekly. Late payments may lead to
                    penalties and interest.{' '}
                  </li>
                  <li>
                    {' '}
                    <b>Tax liability:</b> Report your tax liability correctly to
                    the IRS. Incorrect reporting can cause discrepancies that
                    trigger IRS audits or notices.{' '}
                  </li>

                  <li>
                    {' '}
                    <b> Valid checks:</b> If paying your tax due by check, make
                    sure the check is valid, properly signed, and has sufficient
                    funds.{' '}
                  </li>
                  <li id="receive">
                    {' '}
                    <b>Forms W-2:</b> Employers must issue a copy B of{' '}
                    <Link
                      href="/resources/information-returns/form-w-2-instructions/"
                      sx={{ color: '#2020ff' }}
                    >
                      Form W-2
                    </Link>{' '}
                    to each employee by January 31.{' '}
                  </li>
                  <li>
                    {' '}
                    <b>SSA (Social Security Administration):</b> Employers must
                    send Copy A of each W-2, and the W-3 form to the SSA by
                    January 31.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  What to Do If You Receive a Penalty Notice?
                </Typography>
                <Typography variant="body1">
                  If you get an IRS notice (usually a CP504, CP276B, or CP207),
                  here’s what to do:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <b>Read the Notice Carefully:</b> Understand what the IRS is
                    penalizing.{' '}
                  </li>
                  <li>
                    {' '}
                    <b>Verify Your Filing and Deposits:</b> Check your
                    records.{' '}
                  </li>
                  <li id="penalty">
                    {' '}
                    <b>Contact the IRS Promptly:</b> Don’t ignore it! Timely
                    communication can help.{' '}
                  </li>
                  <li>
                    {' '}
                    <b>Request Abatement if Applicable:</b> File Form 843 to
                    request penalty relief.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  How to Request Penalty Abatement?
                </Typography>

                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    Submit Form 843 to request abatement of assessed penalties
                    or interest.{' '}
                  </li>
                  <li>
                    {' '}
                    If you receive a penalty notice, reply with an explanation
                    and the IRS may waive penalties if you have reasonable
                    cause.{' '}
                  </li>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on IRS guidelines.
                        For more details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i941.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        instructions for Form 941,{' '}
                        <Link
                          href="https://www.irs.gov/payments/failure-to-file-penalty"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        Late filing penalty,{' '}
                        <Link
                          href="https://www.irs.gov/payments/failure-to-pay-penalty"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        Late Payment Penalty,{' '}
                        <Link
                          href="https://www.irs.gov/payments/failure-to-deposit-penalty"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        late deposit Penalty and{' '}
                        <Link
                          href="https://www.irs.gov/payments/accuracy-related-penalty"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        accuracy related penalty.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Act Before It&#39;s Too Late — File Form 941 <br />
              with TaxZerone!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Don’t let penalties put your business down. With <b>TaxZerone</b>,
              you can file
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              Form 941 quickly, accurately, and securely.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941penalties`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form943DueDates;
